import { useEffect, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const LINK_EXPIRY_MINUTES = 15
const RESEND_BUFFER_SECONDS = 2

const formatCountdown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

const remainingFromSentAt = (sentAt, waitSeconds) => {
  if (!sentAt) return 0
  const elapsed = (Date.now() - new Date(sentAt).getTime()) / 1000
  return Math.max(0, waitSeconds - elapsed)
}

export default function CheckEmail({
  email,
  sentAt = null,
  waitSeconds = 300,
  hasPendingRequest = false,
  csrfToken,
  error,
}) {
  const [remaining, setRemaining] = useState(() => remainingFromSentAt(sentAt, waitSeconds))
  const [submitting, setSubmitting] = useState(false)
  const [activationAnnouncement, setActivationAnnouncement] = useState('')
  const wasLockedRef = useRef(remaining > RESEND_BUFFER_SECONDS)

  useEffect(() => {
    if (!hasPendingRequest) return undefined
    const tick = () => setRemaining(remainingFromSentAt(sentAt, waitSeconds))
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [hasPendingRequest, sentAt, waitSeconds])

  useEffect(() => {
    const wasLocked = wasLockedRef.current
    const isUnlocked = remaining <= RESEND_BUFFER_SECONDS
    if (wasLocked && isUnlocked) {
      setActivationAnnouncement('You can now resend the link.')
    }
    wasLockedRef.current = !isUnlocked
  }, [remaining])

  const canResend = hasPendingRequest && remaining <= RESEND_BUFFER_SECONDS
  const announceMinutes = Math.max(0, Math.ceil(remaining / 60))

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Check your email</CardTitle>
          <CardDescription>We sent a magic link to:</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div
              role="alert"
              className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400"
            >
              {error}
            </div>
          )}

          <div className="p-3 rounded-md bg-muted border text-center">
            <p className="text-base font-medium break-all">{email}</p>
          </div>

          <p className="text-sm text-muted-foreground text-center">
            Click the link in your email to sign in. The link expires in {LINK_EXPIRY_MINUTES} minutes.
          </p>

          {hasPendingRequest && !canResend && (
            <div className="text-sm text-muted-foreground text-center" aria-hidden="true">
              You can request another link in{' '}
              <span className="font-mono font-medium tabular-nums">
                {formatCountdown(remaining)}
              </span>
            </div>
          )}

          {/* SR-only: announce remaining time at minute granularity instead of every second */}
          {hasPendingRequest && !canResend && (
            <div role="timer" aria-live="polite" aria-atomic="true" className="sr-only">
              You can request another link in about {announceMinutes} minute{announceMinutes === 1 ? '' : 's'}.
            </div>
          )}

          {/* SR-only: announces once when the resend control activates */}
          <div role="status" aria-live="polite" className="sr-only">
            {activationAnnouncement}
          </div>

          {canResend && (
            <>
              <form
                action="/email-auth-request"
                method="post"
                onSubmit={() => setSubmitting(true)}
              >
                <input type="hidden" name="authenticity_token" value={csrfToken} />
                <input type="hidden" name="email" value={email} />
                <Button
                  type="submit"
                  variant="outline"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send another link'}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground text-center">
                Still didn&rsquo;t receive it? Email{' '}
                <a
                  href="mailto:admin@kingdomfactor.us"
                  className="underline hover:text-primary"
                >
                  admin@kingdomfactor.us
                </a>{' '}
                and we&rsquo;ll get you in.
              </p>
            </>
          )}

          <div className="text-center pt-2">
            <Link href="/login" className="text-sm text-primary hover:underline">
              {hasPendingRequest ? 'Wrong email? Enter a different one' : '← Back to login'}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
